import React, { useState, useEffect, useContext } from 'react';
import { Tooltip, IconButton, Typography, Button, Grid, CircularProgress, TextField as MuiTextField, MenuItem } from '@mui/material';
import { CustomQuillEditor, Dropzone } from 'components';
import { Field, useFormikContext, FieldArray, FieldProps } from 'formik';
import { TextField } from 'formik-mui';
import axios from 'axios';
import { MdAttachFile as AttachmentIcon, MdClose as CancelIcon, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';
import { IMessage, MediaWorkOrder, messageSaveSchema, ticketDetailsContext } from '../../TicketDetailsContext';
import * as yup from 'yup';
import { useIntl } from 'react-intl';
import { LocalizationIds } from 'helpers/Localizations';
import { TicketStatus } from 'helpers/Enums';
import { applicationContext } from 'helpers/services';
import { localeManagerContext } from 'helpers/Locale';
import AddOrders from './AddOrders';
import { useSnackbar } from 'notistack';


interface IProps {
    ticketNumber: string
    editMessage?: IMessage
    onClose?: () => void
}

let MessageEditor = (props: IProps) => {
    let { enqueueSnackbar } = useSnackbar();
    let { formatMessage } = useIntl();

    let { ticket } = useContext(ticketDetailsContext);
    let { ticketStatuses } = useContext(applicationContext);
    let { currentLocale } = useContext(localeManagerContext);

    let [showDropzone, setShowDropzone] = useState<boolean>(false);
    let [uploadingFiles, setUploadingFiles] = useState<string[]>([]);

    type SaveMessage = yup.InferType<typeof messageSaveSchema>;

    let form = useFormikContext<SaveMessage>();

    useEffect(() => {
        form.setFieldValue('newStatus', ticket.status);
    }, [ticket.status]);


    let dropCallback = (files: any[]) => {
        let filesInUpload = files.map(x => x.name);
        setUploadingFiles(filesInUpload);

        files.forEach(async (element: any) => {
            try {
                let formData = new FormData();
                formData.append("file", element);
                formData.append("ticketNumber", props.ticketNumber);

                let res = await axios.post('/api/TicketMessage/UploadAttachment', formData);

                let found = form.values.attachments.find(x => x.fileId === res.data.fileId);

                if (found) {
                    enqueueSnackbar(formatMessage({ id: LocalizationIds.INFO_AttachmentDuplicated }), { variant: 'warning' });
                    return;
                }

                form.values.attachments.push({
                    fileId: res.data.fileId,
                    isDuplicated: res.data.isDuplicated,
                    isGalleryFile: res.data.isGalleryFile,
                    fileName: element.name
                });

                form.setFieldValue('attachments', [...form.values.attachments]);
            } catch (err) {
                form.setFieldError("attachments", "Errore durante l'importazione del file");
            } finally {
                filesInUpload.splice(filesInUpload.indexOf(element.name), 1);
                setUploadingFiles([...filesInUpload])
            }
        })
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Field name="text">
                        {({ field }: FieldProps) =>
                            // per div + bounds in componente vedi: https://github.com/quilljs/quill/issues/360
                            <div data-text-editor="name">
                                <CustomQuillEditor
                                    value={field.value}
                                    onChange={field.onChange(field.name)}
                                />
                            </div>
                        }
                    </Field>
                </Grid>
                <Grid item xs>
                    <Button color="secondary" style={{ marginLeft: 0 }} variant="outlined" onClick={() => setShowDropzone(!showDropzone)}>
                        {showDropzone ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}&nbsp;<AttachmentIcon />
                    </Button>
                </Grid>
                {props.onClose !== undefined &&
                    <Grid item>
                        <Button
                            color="secondary"
                            variant="outlined"
                            disabled={uploadingFiles.length > 0}
                            onClick={props.onClose}
                        >
                            {formatMessage({ id: LocalizationIds.Cancel })}
                        </Button>
                    </Grid>
                }
                {(form.values.messageId == null || form.values.messageId === '') && ticket.isReadOnly === false &&
                    <Grid item>
                        <MuiTextField
                            select
                            label={formatMessage({ id: LocalizationIds.NewStatus })}
                            value={form.values.newStatus}
                            name="newStatus"
                            onChange={(e: any) => {
                                form.setFieldValue('newStatus', e.target.value as any as TicketStatus);
                            }}
                        >
                            {ticketStatuses.map(status =>
                                <MenuItem key={status.code} value={status.code}>{status.descriptions[currentLocale]}</MenuItem>
                            )}
                        </MuiTextField>
                    </Grid>
                }
                <Grid item>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={uploadingFiles.length > 0 || form.isSubmitting}
                        onClick={() => {
                            form.setFieldValue('shareWithCustomer', false);
                            form.submitForm();
                        }}
                    >
                        {formatMessage({ id: LocalizationIds.Save })}
                    </Button>
                </Grid>
                {ticket.customerCanView === true &&
                    <Grid item>
                        <Button
                            variant="contained"
                            disabled={uploadingFiles.length > 0 || form.isSubmitting}
                            style={{ marginRight: 0 }}
                            onClick={() => {
                                form.setFieldValue('shareWithCustomer', true);
                                form.submitForm();
                            }}
                        >
                            {formatMessage({ id: LocalizationIds.SaveAndShare })}
                        </Button>
                    </Grid>
                }

                <Grid item xs={12}>
                    {showDropzone && <Dropzone isMultiple={true} onDropCallback={dropCallback} />}

                    {uploadingFiles.map((x, index) =>
                        <React.Fragment key={index}>
                            <Typography>{formatMessage({ id: LocalizationIds.UploadingFile })}&nbsp;{x}&nbsp;<CircularProgress size={16} /></Typography>
                        </React.Fragment>
                    )}

                    <FieldArray name="attachments">
                        {arrayHelper => form.values.attachments
                            .map((file, index) =>
                                <Typography key={index}>
                                    {formatMessage({ id: LocalizationIds.UploadedFile })}: &nbsp;{file.fileName}
                                    <Tooltip arrow title={formatMessage({ id: LocalizationIds.Remove })}>
                                        <IconButton size="small" onClick={() => arrayHelper.remove(index)}>
                                            <CancelIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Typography>
                            )
                        }
                    </FieldArray>
                </Grid>

                {form.values.attachments.filter(x => x.isDuplicated === false && x.isGalleryFile === true).length > 0 &&
                    <>
                        <Grid item xs={12}>
                            <Typography gutterBottom>Aggiungi queste informazioni aggiuntive che saranno inviate a Gallery</Typography>
                            <Field
                                name="mediaTitle"
                                component={TextField}
                                label="Titolo"
                                fullWidth
                                margin="normal"
                            />

                            <AddOrders
                                ticketNumber={ticket.ticketNumber}
                                selectedRowIds={form.values.mediaWorkOrders.map(x => x.workOrderId)}
                                onSelect={(workOrders: MediaWorkOrder[]) => form.setFieldValue("mediaWorkOrders", workOrders)}
                            />
                        </Grid>
                    </>
                }
            </Grid>
        </>
    )
}

export default MessageEditor;
